import React, { useEffect, useState, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, Save, Camera, CheckCircle, AlertTriangle, 
  FileText, Building2, Upload, Link, ExternalLink,
  ClipboardList, Loader2, Plus, Image, AlertOctagon, Clock, User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Evaluation options for TR-I Stage 2
const evaluationOptions = [
  { value: "conform", label: "Uygun", color: "bg-green-100 text-green-800 border-green-300" },
  { value: "major_nc", label: "Majör", color: "bg-red-100 text-red-800 border-red-300" },
  { value: "minor_nc", label: "Minör", color: "bg-orange-100 text-orange-800 border-orange-300" }
];

const RoyalCertStage2Form = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { getPrimaryColor } = useTenant();
  
  const [template, setTemplate] = useState(null);
  const [auditRecord, setAuditRecord] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("report");
  
  // Combined report + checklist state
  const [headerData, setHeaderData] = useState({});
  const [checklistData, setChecklistData] = useState({});
  const [closureData, setClosureData] = useState({});
  
  // NCR state
  const [ncrData, setNcrData] = useState({});
  
  // Evidence state
  const [evidenceLinks, setEvidenceLinks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newLink, setNewLink] = useState({ link_type: "wetransfer", url: "", description: "", criteria_ids: [] });
  
  const primaryColor = getPrimaryColor();

  // Helper to convert UTC date to Turkey date string
  const utcToTurkeyDate = (isoDateString) => {
    if (!isoDateString) return "";
    if (!isoDateString.includes('T') && !isoDateString.includes('Z')) {
      return isoDateString.split(' ')[0];
    }
    try {
      const date = new Date(isoDateString);
      return date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
    } catch (e) {
      return isoDateString.split('T')[0];
    }
  };

  // Helper to convert audit type code to readable label
  const getAuditTypeLabel = (auditType) => {
    if (!auditType) return "";
    const mapping = {
      "initial": "Initial Certification / İlk Belgelendirme",
      "initial_stage2": "Initial Certification - Stage 2 / İlk Belgelendirme - 2. Aşama",
      "initial_stage3": "Initial Certification - Stage 3 / İlk Belgelendirme - 3. Aşama",
      "surveillance": "Surveillance / Gözetim",
      "surveillance_stage2": "Surveillance - Stage 2 / Gözetim - 2. Aşama",
      "recertification": "Re-Certification / Yeniden Belgelendirme",
      "recertification_stage2": "Re-Certification - Stage 2 / Yeniden Belgelendirme - 2. Aşama",
      "tr_i": "TR-I Denetimi",
      "tr_ii": "TR-II Denetimi"
    };
    return mapping[auditType] || auditType;
  };

  // Helper function to map auto_fill_data to header field IDs
  const mapAutoFillToHeader = (autoFillData, existingHeader = {}) => {
    if (!autoFillData) return existingHeader;
    
    const customer = autoFillData.customer || {};
    const audit = autoFillData.audit || {};
    const planning = autoFillData.planning || {};
    
    return {
      ...existingHeader,
      // Map customer fields
      company_name: existingHeader.company_name || customer.name || "",
      brand_name: existingHeader.brand_name || customer.brand_name || "",
      address: existingHeader.address || customer.address || "",
      phone: existingHeader.phone || customer.phone || "",
      email: existingHeader.email || customer.email || "",
      room_count: existingHeader.room_count || customer.room_count || "",
      employee_count: existingHeader.employee_count || customer.employee_count || "",
      // Map audit fields - use UTC conversion for dates
      audit_date: existingHeader.audit_date || utcToTurkeyDate(audit.scheduled_date) || "",
      audit_duration: existingHeader.audit_duration || audit.duration_days || "",
      audit_type: existingHeader.audit_type || getAuditTypeLabel(audit.audit_type) || "",
      team_leader: existingHeader.team_leader || planning.audit_team?.team_leader || audit.team_leader || "",
      scope: existingHeader.scope || audit.scope || "Hotel and Accommodation Services",
    };
  };

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Get audit info
        const auditRes = await axios.get(`${API}/audits/${auditId}`, { withCredentials: true });
        setAudit(auditRes.data);
        
        // Get template
        const templateRes = await axios.get(`${API}/audit-templates/royalcert_stage2_audit`, { withCredentials: true });
        setTemplate(templateRes.data);
        
        // Check if audit record exists
        try {
          const recordRes = await axios.get(`${API}/royalcert-audits?audit_id=${auditId}`, { withCredentials: true });
          const existingRecord = recordRes.data.find(r => r.audit_id === auditId && r.template_id === "royalcert_stage2_audit");
          
          if (existingRecord) {
            const fullRecord = await axios.get(`${API}/royalcert-audits/${existingRecord.audit_record_id}`, { withCredentials: true });
            setAuditRecord(fullRecord.data);
            // Map auto_fill_data to header fields, prioritizing existing header data
            const mappedHeader = mapAutoFillToHeader(
              fullRecord.data.auto_fill_data, 
              fullRecord.data.report_data?.header_data || {}
            );
            setHeaderData(mappedHeader);
            setChecklistData(fullRecord.data.checklist_data?.criteria_evaluations || {});
            setClosureData(fullRecord.data.report_data?.closure_data || {});
            setNcrData(fullRecord.data.ncr_data || {});
            setEvidenceLinks(fullRecord.data.evidence?.links || []);
            setPhotos(fullRecord.data.evidence?.photos || []);
          } else {
            // Create new record for stage 2
            const createRes = await axios.post(`${API}/royalcert-audits`, {
              audit_id: auditId,
              template_id: "royalcert_stage2_audit",
              title: `${auditRes.data.customer_name} - 2. Aşama Denetim`
            }, { withCredentials: true });
            
            setAuditRecord(createRes.data);
            // Map auto_fill_data to header fields for new record
            const mappedHeader = mapAutoFillToHeader(createRes.data.auto_fill_data);
            setHeaderData(mappedHeader);
          }
        } catch (err) {
          const createRes = await axios.post(`${API}/royalcert-audits`, {
            audit_id: auditId,
            template_id: "royalcert_stage2_audit",
            title: `${auditRes.data.customer_name} - 2. Aşama Denetim`
          }, { withCredentials: true });
          
          setAuditRecord(createRes.data);
          // Map auto_fill_data to header fields for new record
          const mappedHeader = mapAutoFillToHeader(createRes.data.auto_fill_data);
          setHeaderData(mappedHeader);
        }
        
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Veriler yüklenirken hata oluştu");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [auditId]);

  // Save report (header + checklist + closure)
  const saveReport = useCallback(async () => {
    if (!auditRecord) return;
    
    try {
      setSaving(true);
      
      // Save checklist
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/checklist`,
        { criteria_evaluations: checklistData },
        { withCredentials: true }
      );
      
      // Save report
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/report`,
        { 
          header_data: headerData,
          report_sections: {},
          certificate_decision: {},
          closure_data: closureData
        },
        { withCredentials: true }
      );
      
      toast.success("Rapor kaydedildi");
    } catch (error) {
      console.error("Error saving report:", error);
      toast.error("Rapor kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }, [auditRecord, headerData, checklistData, closureData]);

  // Save NCR data
  const saveNcrData = useCallback(async () => {
    if (!auditRecord) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/ncr`,
        { ncr_responses: ncrData },
        { withCredentials: true }
      );
      toast.success("Uygunsuzluk verileri kaydedildi");
    } catch (error) {
      console.error("Error saving NCR:", error);
      toast.error("Uygunsuzluk verileri kaydedilemedi");
    } finally {
      setSaving(false);
    }
  }, [auditRecord, ncrData]);

  // Get nonconformities from checklist
  const getNonconformities = useCallback(() => {
    const ncs = [];
    if (!template?.checklist?.sections) return ncs;
    
    template.checklist.sections.forEach(section => {
      section.criteria.forEach(criteria => {
        const evaluation = checklistData[criteria.id];
        if (evaluation?.status === 'major_nc' || evaluation?.status === 'minor_nc') {
          ncs.push({
            criteria_id: criteria.id,
            criteria_title: criteria.title,
            section_title: section.title,
            classification: evaluation.status,
            description: evaluation.notes || '',
            evidence: evaluation.evidence || ''
          });
        }
      });
    });
    
    return ncs;
  }, [template, checklistData]);

  // Update checklist criteria
  const updateCriteria = (criteriaId, field, value) => {
    setChecklistData(prev => ({
      ...prev,
      [criteriaId]: {
        ...prev[criteriaId],
        [field]: value
      }
    }));
  };

  // Update NCR response
  const updateNcrResponse = (criteriaId, field, value) => {
    setNcrData(prev => ({
      ...prev,
      [criteriaId]: {
        ...prev[criteriaId],
        [field]: value
      }
    }));
  };

  // Add evidence link
  const addEvidenceLink = async () => {
    if (!newLink.url) {
      toast.error("Link URL'si gerekli");
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/evidence-link`,
        newLink,
        { withCredentials: true }
      );
      setEvidenceLinks([...evidenceLinks, res.data.link]);
      setNewLink({ link_type: "wetransfer", url: "", description: "", criteria_ids: [] });
      toast.success("Link eklendi");
    } catch (error) {
      toast.error("Link eklenemedi");
    }
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Dosya boyutu 25MB'dan büyük olamaz");
      return;
    }
    
    try {
      setUploadingPhoto(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("description", "");
      
      const res = await axios.post(
        `${API}/royalcert-audits/${auditRecord.audit_record_id}/photo`,
        formData,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      setPhotos([...photos, res.data.photo]);
      toast.success("Fotoğraf yüklendi");
    } catch (error) {
      toast.error("Fotoğraf yüklenemedi");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Calculate summary
  const getChecklistSummary = () => {
    const values = Object.values(checklistData);
    return {
      conform: values.filter(v => v.status === "conform").length,
      major_nc: values.filter(v => v.status === "major_nc").length,
      minor_nc: values.filter(v => v.status === "minor_nc").length,
      total: template?.checklist?.sections?.reduce((acc, s) => acc + s.criteria.length, 0) || 0
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  const summary = getChecklistSummary();

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {audit?.customer_name} - 2. Aşama Denetim
                </h1>
                <p className="text-sm text-gray-500">TR-I Endüstri Kriterleri</p>
              </div>
            </div>
            
            {/* Summary badges */}
            <div className="hidden md:flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800">{summary.conform} Uygun</Badge>
              <Badge className="bg-red-100 text-red-800">{summary.major_nc} Majör</Badge>
              <Badge className="bg-orange-100 text-orange-800">{summary.minor_nc} Minör</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="report" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">Denetim Raporu</span>
              <span className="sm:hidden">FR.04</span>
            </TabsTrigger>
            <TabsTrigger value="ncr" className="flex items-center gap-2">
              <AlertOctagon className="w-4 h-4" />
              <span className="hidden sm:inline">Uygunsuzluklar</span>
              <span className="sm:hidden">NCR</span>
              {getNonconformities().length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white">
                  {getNonconformities().length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="evidence" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Kanıtlar
            </TabsTrigger>
          </TabsList>

          {/* REPORT TAB (Combined Report + Checklist) */}
          <TabsContent value="report">
            <div className="space-y-6">
              {/* Header Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Denetim Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {template?.report?.header_fields?.map((field) => (
                      <div key={field.id}>
                        <Label className="text-sm">{field.label}</Label>
                        {field.type === "textarea" ? (
                          <Textarea
                            value={headerData[field.id] || field.default || ""}
                            onChange={(e) => setHeaderData({...headerData, [field.id]: e.target.value})}
                            className="mt-1"
                            disabled={field.auto_fill}
                          />
                        ) : field.type === "select" ? (
                          <Select
                            value={headerData[field.id] || field.default || ""}
                            onValueChange={(v) => setHeaderData({...headerData, [field.id]: v})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.map((opt) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                            value={headerData[field.id] || field.default || ""}
                            onChange={(e) => setHeaderData({...headerData, [field.id]: e.target.value})}
                            className="mt-1"
                            disabled={field.auto_fill}
                          />
                        )}
                        {field.auto_fill && (
                          <p className="text-xs text-gray-400 mt-1">Otomatik dolduruldu</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Checklist */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>TR-I Endüstri Kriterleri</CardTitle>
                    <CardDescription>Her kriter için değerlendirme yapın</CardDescription>
                  </div>
                  <Button onClick={saveReport} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Kaydet
                  </Button>
                </CardHeader>
                <CardContent>
                  <Accordion type="multiple" className="space-y-4">
                    {template?.checklist?.sections?.map((section) => (
                      <AccordionItem key={section.id} value={section.id} className="border rounded-lg px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-left">{section.title}</span>
                            <Badge variant="outline">{section.criteria.length} kriter</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4 pt-4">
                            {section.criteria.map((criteria) => (
                              <div key={criteria.id} className="border rounded-lg p-4 bg-gray-50">
                                <div className="flex items-start justify-between gap-4 mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge style={{ backgroundColor: primaryColor, color: 'white' }}>
                                        {criteria.id}
                                      </Badge>
                                      <span className="font-medium">{criteria.title}</span>
                                    </div>
                                    {criteria.questions && (
                                      <ul className="text-sm text-gray-600 mt-2 space-y-1 list-disc list-inside">
                                        {criteria.questions.slice(0, 3).map((q, idx) => (
                                          <li key={idx}>{q}</li>
                                        ))}
                                        {criteria.questions.length > 3 && (
                                          <li className="text-gray-400">+{criteria.questions.length - 3} daha...</li>
                                        )}
                                      </ul>
                                    )}
                                  </div>
                                  <Select
                                    value={checklistData[criteria.id]?.status || ""}
                                    onValueChange={(v) => updateCriteria(criteria.id, "status", v)}
                                  >
                                    <SelectTrigger className="w-32">
                                      <SelectValue placeholder="Seçin" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {evaluationOptions.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          <span className={`px-2 py-0.5 rounded ${opt.color}`}>
                                            {opt.label}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs text-gray-500">Objektif Delile Dayalı Değerlendirme</Label>
                                    <Textarea
                                      placeholder="Gözlemlenen kanıtları yazın..."
                                      value={checklistData[criteria.id]?.evidence || ""}
                                      onChange={(e) => updateCriteria(criteria.id, "evidence", e.target.value)}
                                      className="mt-1 h-20"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-500">
                                      {(checklistData[criteria.id]?.status === 'major_nc' || checklistData[criteria.id]?.status === 'minor_nc') 
                                        ? "Uygunsuzluk Gözlem *" 
                                        : "Notlar"}
                                    </Label>
                                    <Textarea
                                      placeholder={
                                        (checklistData[criteria.id]?.status === 'major_nc' || checklistData[criteria.id]?.status === 'minor_nc')
                                          ? "Tespit edilen uygunsuzluğu detaylı açıklayın..."
                                          : "Ek notlar..."
                                      }
                                      value={checklistData[criteria.id]?.notes || ""}
                                      onChange={(e) => updateCriteria(criteria.id, "notes", e.target.value)}
                                      className={`mt-1 h-20 ${
                                        (checklistData[criteria.id]?.status === 'major_nc' || checklistData[criteria.id]?.status === 'minor_nc')
                                          ? 'border-orange-300 focus:border-orange-500'
                                          : ''
                                      }`}
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>

              {/* Closure */}
              <Card>
                <CardHeader>
                  <CardTitle>Denetim Kapanışı</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Ekip Lideri Ad/Soyad</Label>
                      <Input
                        value={closureData.team_leader_name || headerData.team_leader || ""}
                        onChange={(e) => setClosureData({...closureData, team_leader_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Tarih</Label>
                      <Input
                        type="date"
                        value={closureData.closure_date || ""}
                        onChange={(e) => setClosureData({...closureData, closure_date: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* NCR TAB */}
          <TabsContent value="ncr">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <AlertOctagon className="w-5 h-5 text-red-600" />
                    Uygunsuzluk Bildirim Formu (FR.07 TR-I)
                  </CardTitle>
                  <CardDescription>
                    Denetim raporunda tespit edilen uygunsuzluklar
                  </CardDescription>
                </div>
                <Button onClick={saveNcrData} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Kaydet
                </Button>
              </CardHeader>
              <CardContent>
                {getNonconformities().length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">Uygunsuzluk Yok</h3>
                    <p className="text-gray-500 mt-2">
                      Denetim raporunda Majör veya Minör olarak işaretlenen kriter bulunmuyor.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary */}
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-sm font-medium text-red-800">
                          {getNonconformities().filter(nc => nc.classification === 'major_nc').length} Majör
                        </span>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        <span className="text-sm font-medium text-orange-800">
                          {getNonconformities().filter(nc => nc.classification === 'minor_nc').length} Minör
                        </span>
                      </div>
                    </div>

                    {/* NCR List */}
                    <div className="space-y-4">
                      {getNonconformities().map((nc, index) => (
                        <div 
                          key={nc.criteria_id} 
                          className={`border rounded-lg overflow-hidden ${
                            nc.classification === 'major_nc' ? 'border-red-300' : 'border-orange-300'
                          }`}
                        >
                          {/* Auditor Section */}
                          <div className={`p-4 ${
                            nc.classification === 'major_nc' ? 'bg-red-50' : 'bg-orange-50'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <span className={`px-2 py-1 text-xs font-bold rounded ${
                                  nc.classification === 'major_nc' 
                                    ? 'bg-red-500 text-white' 
                                    : 'bg-orange-500 text-white'
                                }`}>
                                  #{index + 1}
                                </span>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Badge style={{ backgroundColor: primaryColor, color: 'white' }}>
                                      {nc.criteria_id}
                                    </Badge>
                                    <span className={`px-2 py-0.5 text-xs rounded ${
                                      nc.classification === 'major_nc'
                                        ? 'bg-red-200 text-red-800'
                                        : 'bg-orange-200 text-orange-800'
                                    }`}>
                                      {nc.classification === 'major_nc' ? 'Majör' : 'Minör'}
                                    </span>
                                  </div>
                                  <p className="font-medium text-gray-900 mt-1">{nc.criteria_title}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {ncrData[nc.criteria_id]?.status === 'closed' ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Kapatıldı
                                  </Badge>
                                ) : ncrData[nc.criteria_id]?.root_cause_analysis ? (
                                  <Badge className="bg-blue-100 text-blue-800">
                                    <Clock className="w-3 h-3 mr-1" />
                                    Yanıt Bekliyor
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-800">
                                    <AlertTriangle className="w-3 h-3 mr-1" />
                                    Açık
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            {/* Auditor fields */}
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-gray-600">Tanım (Uygunsuzluk Açıklaması)</Label>
                                <p className="mt-1 text-sm bg-white p-2 rounded border min-h-[60px]">
                                  {nc.description || <span className="text-gray-400 italic">Açıklama girilmemiş</span>}
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-600">Objektif Kanıt</Label>
                                <p className="mt-1 text-sm bg-white p-2 rounded border min-h-[60px]">
                                  {nc.evidence || <span className="text-gray-400 italic">Kanıt girilmemiş</span>}
                                </p>
                              </div>
                            </div>

                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <Label className="text-xs text-gray-600">Sorumlu</Label>
                                <Input
                                  placeholder="İsim"
                                  value={ncrData[nc.criteria_id]?.responsible_person || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'responsible_person', e.target.value)}
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-600">Kapatma Termin Süresi</Label>
                                <Input
                                  type="date"
                                  value={ncrData[nc.criteria_id]?.closure_deadline || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'closure_deadline', e.target.value)}
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-600">Ekip Lideri Serbest Bırakma</Label>
                                <Input
                                  placeholder="Ad Soyad"
                                  value={ncrData[nc.criteria_id]?.team_leader_release || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'team_leader_release', e.target.value)}
                                  className="mt-1 bg-white"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-600">Durum</Label>
                                <Select
                                  value={ncrData[nc.criteria_id]?.status || 'open'}
                                  onValueChange={(v) => updateNcrResponse(nc.criteria_id, 'status', v)}
                                >
                                  <SelectTrigger className="mt-1 bg-white">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="open">Açık</SelectItem>
                                    <SelectItem value="in_progress">İşlemde</SelectItem>
                                    <SelectItem value="pending_verification">Doğrulama Bekliyor</SelectItem>
                                    <SelectItem value="closed">Kapatıldı</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>

                          {/* Client Response Section */}
                          <div className="p-4 bg-white border-t">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-4">
                              Müşteri Yanıtı (Bu kısım müşteri tarafından doldurulacaktır)
                            </p>
                            
                            <div className="space-y-4">
                              <div>
                                <Label className="text-sm font-medium">Düzeltme Kanıt (Doküman, Kayıt, Foto, vd.)</Label>
                                <Textarea
                                  placeholder="Düzeltme kanıtlarını açıklayın..."
                                  value={ncrData[nc.criteria_id]?.correction_evidence || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'correction_evidence', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              
                              <div>
                                <Label className="text-sm font-medium">Kök Neden Analizi</Label>
                                <Textarea
                                  placeholder="Uygunsuzluğun kök nedenini analiz edin..."
                                  value={ncrData[nc.criteria_id]?.root_cause_analysis || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'root_cause_analysis', e.target.value)}
                                  className="mt-1"
                                />
                              </div>
                              
                              <div>
                                <Label className="text-sm font-medium">Düzeltici Faaliyetler</Label>
                                <Textarea
                                  placeholder="Tekrarını önlemek için planlanan faaliyetleri açıklayın..."
                                  value={ncrData[nc.criteria_id]?.corrective_actions || ''}
                                  onChange={(e) => updateNcrResponse(nc.criteria_id, 'corrective_actions', e.target.value)}
                                  className="mt-1"
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                                <div>
                                  <Label className="text-sm font-medium">Kuruluş Yetkilisi Adı</Label>
                                  <Input
                                    placeholder="Yetkili adı"
                                    value={ncrData[nc.criteria_id]?.authority_name || ''}
                                    onChange={(e) => updateNcrResponse(nc.criteria_id, 'authority_name', e.target.value)}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-sm font-medium">İmza Tarihi</Label>
                                  <Input
                                    type="date"
                                    value={ncrData[nc.criteria_id]?.authority_signature_date || ''}
                                    onChange={(e) => updateNcrResponse(nc.criteria_id, 'authority_signature_date', e.target.value)}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EVIDENCE TAB */}
          <TabsContent value="evidence">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Photos */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Saha Fotoğrafları
                  </CardTitle>
                  <CardDescription>Max 25MB/dosya - JPEG, PNG, WebP</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="photo-upload-stage2"
                      />
                      <label htmlFor="photo-upload-stage2" className="cursor-pointer">
                        {uploadingPhoto ? (
                          <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                        ) : (
                          <Upload className="w-8 h-8 mx-auto text-gray-400" />
                        )}
                        <p className="mt-2 text-sm text-gray-600">
                          Fotoğraf yüklemek için tıklayın
                        </p>
                      </label>
                    </div>

                    {photos.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {photos.map((photo, idx) => (
                          <div key={photo.photo_id || idx} className="border rounded-lg p-2">
                            <div className="aspect-video bg-gray-100 rounded flex items-center justify-center">
                              <Image className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-xs text-gray-600 mt-1 truncate">{photo.filename}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Links */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link className="w-5 h-5" />
                    Kanıt Linkleri
                  </CardTitle>
                  <CardDescription>WeTransfer veya Google Drive linkleri</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="border rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Link Türü</Label>
                          <Select
                            value={newLink.link_type}
                            onValueChange={(v) => setNewLink({...newLink, link_type: v})}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="wetransfer">WeTransfer</SelectItem>
                              <SelectItem value="google_drive">Google Drive</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">İlgili Kriterler</Label>
                          <Input
                            placeholder="A1, B2, C3..."
                            value={newLink.criteria_ids.join(", ")}
                            onChange={(e) => setNewLink({
                              ...newLink, 
                              criteria_ids: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                            })}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">URL</Label>
                        <Input
                          placeholder="https://..."
                          value={newLink.url}
                          onChange={(e) => setNewLink({...newLink, url: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Açıklama</Label>
                        <Input
                          placeholder="Link içeriği hakkında..."
                          value={newLink.description}
                          onChange={(e) => setNewLink({...newLink, description: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <Button onClick={addEvidenceLink} className="w-full" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Link Ekle
                      </Button>
                    </div>

                    {evidenceLinks.length > 0 && (
                      <div className="space-y-2">
                        {evidenceLinks.map((link, idx) => (
                          <div key={link.link_id || idx} className="border rounded-lg p-3 flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {link.link_type === "wetransfer" ? "WeTransfer" : "Google Drive"}
                                </Badge>
                                {link.criteria_ids?.map(c => (
                                  <Badge key={c} className="text-xs" style={{ backgroundColor: primaryColor, color: 'white' }}>
                                    {c}
                                  </Badge>
                                ))}
                              </div>
                              <p className="text-sm text-gray-600 mt-1 truncate">{link.description || link.url}</p>
                            </div>
                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Save Button */}
      <div className="fixed bottom-6 right-6 flex gap-2">
        <Button 
          size="lg" 
          onClick={() => {
            if (activeTab === "report") saveReport();
            else if (activeTab === "ncr") saveNcrData();
          }}
          disabled={saving}
          style={{ backgroundColor: primaryColor }}
          className="text-white shadow-lg"
        >
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          Kaydet
        </Button>
      </div>
    </div>
  );
};

export default RoyalCertStage2Form;
